import React from 'react';
import { ArrowRight, Server, Bot, Layers, Smartphone, TrendingUp } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const solutions = [
  {
    id: 'enterprise',
    number: '01',
    icon: Server,
    title: 'Enterprise SaaS Platforms',
    description: 'Revenue-generating systems built for scale.',
    details: 'We build multi-tenant SaaS platforms with enterprise-grade security, 99.9% uptime SLA, and architecture designed to scale from day one.',
  },
  {
    id: 'ai-automation',
    number: '02',
    icon: Bot,
    title: 'AI-Powered Operations',
    description: 'Reduce costs with intelligent automation.',
    details: 'Deploy AI agents that handle complex workflows autonomously — from customer support to data processing — freeing your team for high-value strategic work.',
  },
  {
    id: 'saas',
    number: '03',
    icon: Layers,
    title: 'Product-Led Growth',
    description: 'From MVP to market-ready product, faster.',
    details: 'End-to-end SaaS development with built-in growth mechanics: self-serve onboarding, usage-based billing, and viral loops designed to lower acquisition costs.',
  },
  {
    id: 'mobile-web',
    number: '04',
    icon: Smartphone,
    title: 'High-Conversion Applications',
    description: 'Apps that drive engagement and revenue.',
    details: 'Native and cross-platform applications optimized for conversion — with performance, UX, and analytics that turn users into paying customers.',
  },
  {
    id: 'growth',
    number: '05',
    icon: TrendingUp,
    title: 'Scalable Growth Systems',
    description: 'Smarter customer acquisition, lower CAC.',
    details: 'Data-driven acquisition engines combining SEO, paid media, automation, and CRO — built for predictable, profitable growth at scale.',
  },
];

const SolutionCard = ({ solution, index, onOpenContactModal }) => {
  return (
    <div
      className="group relative py-4 lg:py-6 border-b transition-colors duration-300 hover:bg-gray-50/30"
      style={{ borderColor: '#E2E8F0' }}
      data-testid={`solution-card-${solution.id}`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
        {/* Number & Icon Column */}
        <div className="lg:col-span-2 flex items-center gap-4 lg:gap-0 lg:flex-col lg:items-start">
          <span 
            className="text-4xl lg:text-5xl font-bold tracking-tight opacity-10"
            style={{ color: '#0A3D91' }}
          >
            {solution.number}
          </span>
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center lg:mt-2"
            style={{ backgroundColor: 'rgba(10, 61, 145, 0.05)' }}
          >
            <solution.icon 
              className="w-5 h-5"
              style={{ color: '#0A3D91' }}
            />
          </div>
        </div>

        {/* Content Column */}
        <div className="lg:col-span-7">
          <h3
            className="text-xl sm:text-2xl font-bold tracking-tight mb-2"
            style={{ color: '#131B2E' }}
          >
            {solution.title}
          </h3>
          <p
            className="text-base font-medium mb-1"
            style={{ color: '#0A3D91' }}
          >
            {solution.description}
          </p>
          <p
            className="text-sm leading-relaxed"
            style={{ color: '#475569' }}
          >
            {solution.details}
          </p>
        </div>

        {/* CTA Column */}
        <div className="lg:col-span-3 lg:text-right">
          <button
            onClick={() => onOpenContactModal(`I'm interested in ${solution.title}. I'd like to discuss how this can help my business.`)}
            className="inline-flex items-center gap-2 text-sm font-semibold transition-all duration-200 group/cta cursor-pointer bg-transparent border-none"
            style={{ color: '#0A3D91' }}
            data-testid={`solution-cta-${solution.id}`}
          >
            <span className="relative">
              Explore Solution
              <span 
                className="absolute -bottom-0.5 left-0 w-0 h-0.5 transition-all duration-200 group-hover/cta:w-full"
                style={{ backgroundColor: '#00BFFF' }}
              />
            </span>
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover/cta:translate-x-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const SolutionsSection = ({ onOpenContactModal }) => {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="solutions"
      ref={sectionRef}
      data-testid="solutions-section"
      className="relative"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="kv-container">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-14">
          {/* Section Header */}
          <div className="kv-reveal max-w-3xl mb-6">
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
              style={{ color: '#0A3D91' }}
              data-testid="solutions-label"
            >
              Solutions
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: '#131B2E', letterSpacing: '-0.025em' }}
              data-testid="solutions-headline"
            >
              Outcomes That Drive Growth
            </h2>
            <p
              className="text-lg"
              style={{ color: '#475569', lineHeight: '1.7' }}
            >
              Every engagement is designed around measurable business impact — 
              revenue growth, cost reduction, and scalable efficiency.
            </p>
          </div>

          {/* Solutions List */}
          <div 
            className="kv-reveal border-t"
            style={{ borderColor: '#E2E8F0' }}
            data-testid="solutions-list"
          >
            {solutions.map((solution, index) => (
              <SolutionCard key={solution.id} solution={solution} index={index} onOpenContactModal={onOpenContactModal} />
            ))}
          </div>

          {/* Bottom CTA */}
          <div 
            className="kv-reveal pt-6 text-center"
            data-testid="solutions-bottom-cta"
          >
            <p
              className="text-lg sm:text-xl font-semibold mb-2"
              style={{ color: '#131B2E' }}
            >
              Ready to accelerate your growth?
            </p>
            <p
              className="text-sm mb-6 max-w-xl mx-auto"
              style={{ color: '#475569' }}
            >
              Book a strategy call to discuss how we can deliver measurable 
              ROI for your business.
            </p>
            <button
              onClick={onOpenContactModal}
              className="kv-btn-primary px-7 py-3 text-base"
              data-testid="solutions-contact-cta"
            >
              Book a Strategy Call
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
